import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Star, Trash2, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useDeletePhoto, useSetPrimaryPhoto } from '@/hooks/usePhotos';
import type { PhotoResponse, PhotoParentType } from '@/types/photos';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface PhotoGalleryProps {
  photos: PhotoResponse[];
  parentType: PhotoParentType;
  parentId: string;
  editable?: boolean;
  className?: string;
  onPhotoDeleted?: (photoId: string) => void;
  onPrimaryPhotoChanged?: (photoId: string) => void;
}

export function PhotoGallery({
  photos,
  parentType,
  parentId,
  editable = false,
  className,
  onPhotoDeleted,
  onPrimaryPhotoChanged
}: PhotoGalleryProps) {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
  const deletePhoto = useDeletePhoto();
  const setPrimaryPhoto = useSetPrimaryPhoto();

  const sortedPhotos = [...photos].sort((a, b) => {
    if (a.isPrimary && !b.isPrimary) return -1;
    if (!a.isPrimary && b.isPrimary) return 1;
    return a.sortOrder - b.sortOrder;
  });

  const handleDelete = async (photoId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm('Are you sure you want to delete this photo?')) {
      await deletePhoto.mutateAsync(photoId);
      onPhotoDeleted?.(photoId);
    }
  };

  const handleSetPrimary = async (photoId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await setPrimaryPhoto.mutateAsync({ parentType, parentId, photoId });
    onPrimaryPhotoChanged?.(photoId);
  };

  const openLightbox = (index: number) => {
    setSelectedPhotoIndex(index);
  };

  const closeLightbox = () => {
    setSelectedPhotoIndex(null);
  };

  const goToPrevious = () => {
    if (selectedPhotoIndex !== null && selectedPhotoIndex > 0) {
      setSelectedPhotoIndex(selectedPhotoIndex - 1);
    }
  };

  const goToNext = () => {
    if (selectedPhotoIndex !== null && selectedPhotoIndex < sortedPhotos.length - 1) {
      setSelectedPhotoIndex(selectedPhotoIndex + 1);
    }
  };

  if (photos.length === 0) {
    return (
      <div className={cn('text-center py-8 text-muted-foreground', className)}>
        No photos yet
      </div>
    );
  }

  return (
    <>
      <div className={cn('grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4', className)}>
        {sortedPhotos.map((photo, index) => (
          <div
            key={photo.id}
            className="relative group cursor-pointer aspect-square rounded-lg overflow-hidden bg-muted"
            onClick={() => openLightbox(index)}
          >
            <img
              src={photo.url}
              alt={photo.altText || `Photo ${index + 1}`}
              className="w-full h-full object-cover transition-transform group-hover:scale-105"
            />

            {/* Primary badge */}
            {photo.isPrimary && (
              <div className="absolute top-2 left-2 bg-primary text-primary-foreground px-2 py-1 rounded text-xs font-medium flex items-center gap-1">
                <Star className="w-3 h-3 fill-current" />
                Primary
              </div>
            )}

            {/* Hover overlay with actions */}
            {editable && (
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                {!photo.isPrimary && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={(e) => handleSetPrimary(photo.id, e)}
                    disabled={setPrimaryPhoto.isPending}
                  >
                    <Star className="w-4 h-4 mr-1" />
                    Set Primary
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={(e) => handleDelete(photo.id, e)}
                  disabled={deletePhoto.isPending}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Lightbox modal */}
      <Dialog open={selectedPhotoIndex !== null} onOpenChange={() => closeLightbox()}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              Photo {selectedPhotoIndex !== null ? selectedPhotoIndex + 1 : 0} of {sortedPhotos.length}
            </DialogTitle>
          </DialogHeader>

          {selectedPhotoIndex !== null && sortedPhotos[selectedPhotoIndex] && (
            <div className="relative">
              <img
                src={sortedPhotos[selectedPhotoIndex].url}
                alt={sortedPhotos[selectedPhotoIndex].altText || `Photo ${selectedPhotoIndex + 1}`}
                className="w-full h-auto max-h-[70vh] object-contain"
              />

              {/* Navigation buttons */}
              {sortedPhotos.length > 1 && (
                <>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="absolute left-2 top-1/2 -translate-y-1/2"
                    onClick={goToPrevious}
                    disabled={selectedPhotoIndex === 0}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                    onClick={goToNext}
                    disabled={selectedPhotoIndex === sortedPhotos.length - 1}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </>
              )}

              {/* Photo info */}
              {sortedPhotos[selectedPhotoIndex].altText && (
                <p className="text-sm text-muted-foreground mt-2 text-center">
                  {sortedPhotos[selectedPhotoIndex].altText}
                </p>
              )}

              {/* Actions in lightbox */}
              {editable && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  {!sortedPhotos[selectedPhotoIndex].isPrimary && (
                    <Button
                      variant="secondary"
                      onClick={(e) => handleSetPrimary(sortedPhotos[selectedPhotoIndex].id, e)}
                      disabled={setPrimaryPhoto.isPending}
                    >
                      <Star className="w-4 h-4 mr-2" />
                      Set as Primary
                    </Button>
                  )}
                  <Button
                    variant="destructive"
                    onClick={(e) => {
                      handleDelete(sortedPhotos[selectedPhotoIndex].id, e);
                      closeLightbox();
                    }}
                    disabled={deletePhoto.isPending}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Photo
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
